import { format, parseISO } from "date-fns";
import React from "react";

import { AvatarProfile, Spinner } from "@performyard/py-components";
// Types
import type {
	ResourceLogModelFrontend,
	ReviewCycleAutomatedAssignedSubjectDataFrontend,
} from "@performyard/service-contracts";

// Components
import Button from "../../../components/Button";
import { Grid } from "../../../components/Grid";
import Text from "../../../components/Text";
import TruncatedText from "../../../components/Text/TruncatedText";

// Hooks
import { useUser } from "../../../api/hooks";

// Utils
import get from "../../../utils/GetNested";

// CSS
import css from "./AssignmentLog.module.scss";

/**
 * Loading spinner component.
 */
const spinner = (
	<Grid className={css.spinnerCell}>
		<Spinner className={css.spinner} size={20} data-testid="spinner" />
	</Grid>
);

/**
 * Returns a formatted string for the criteria value.
 */
const formatCriteriaValue = (criteria: ReviewCycleAutomatedAssignedSubjectDataFrontend["criteria"][0]) => {
	const { value, valueName } = criteria;

	if (typeof value === "string") {
		const parsedDate = parseISO(value);

		if (parsedDate.toString() !== "Invalid Date") {
			return format(parsedDate, "MMM d, yyyy").toString();
		}
	}
	return `${valueName ?? value}`;
};

/**
 * Return Person component for subject.
 */
export const getSubjectCell = (resourceLog: ResourceLogModelFrontend) => {
	const subjectId: string | null = get(resourceLog, ["data", "subjectId"], null);
	if (!subjectId) return null;

	const { data: userData, isLoading: isUserDataLoading } = useUser({ userID: subjectId });

	if (isUserDataLoading) {
		return spinner;
	}
	return (
		<div className={css.subjectCell}>
			<AvatarProfile
				employeeId={subjectId}
				avatarSize={20}
				employeeName={userData?.name || "Unknown"}
				forceHardReload
			/>
		</div>
	);
};

/**
 * Return type of of change with manual assignment criteria.
 */
const getCriteriaManual = (resourceLog: ResourceLogModelFrontend) => {
	let content: React.ReactNode;

	// If criteria string is provided, it's a public API change.
	if ("criteria" in resourceLog.data && typeof resourceLog.data.criteria === "string") {
		content = getPublicApiCriteriaContent(resourceLog);
	} else {
		content = getManuallyAssignedByCriteriaContent(resourceLog);
	}

	return (
		<Grid className={css.manuallyAssignedBox} cols={2} gap={1}>
			{content}
		</Grid>
	);
};

/**
 * Return criteria string for public API changes.
 *
 * User's can only be added (not removed) via Public API.
 */
const getPublicApiCriteriaContent = (resourceLog: ResourceLogModelFrontend) => {
	const { data } = resourceLog;
	if ("criteria" in data && typeof data.criteria === "string") {
		return (
			<Text size="paragraph-3" weight="regular">
				{data.criteria}
			</Text>
		);
	}
	return null;
};

/**
 * Return type of of change with Person component for "changedBy" user.
 */
const getManuallyAssignedByCriteriaContent = (resourceLog: ResourceLogModelFrontend) => {
	/* Manual assignment */
	const { changedBy, changeType } = resourceLog;
	const changeTypeString = changeType === "add-subject" ? "Added" : "Removed";

	const { data: userData, isLoading: isUserDataLoading } = changedBy
		? useUser({ userID: changedBy })
		: { data: { name: "Unknown" }, isLoading: false };

	if (isUserDataLoading) {
		return spinner;
	}
	return (
		<>
			<Text className={css.assignedText} size="paragraph-3" weight="regular">
				{changeTypeString} manually by:
			</Text>
			<AvatarProfile
				employeeId={changedBy || "UK"}
				avatarSize={20}
				employeeName={userData?.name || "Unknown"}
				noLink={changedBy === null}
			/>
		</>
	);
};

/**
 * Return truncated summary of automation criteria.
 */
const getCriteriaAutomated = (resourceLog: ResourceLogModelFrontend) => {
	const criteriaData: ReviewCycleAutomatedAssignedSubjectDataFrontend["criteria"] = get(
		resourceLog,
		["data", "criteria"],
		[]
	);

	const criteriaItemDelimiter = "   "; // 3 tactical spaces
	return (
		<div className={css.automatedAssignedBox}>
			<TruncatedText
				className={css.assignedCriteriaItem}
				maxWidth={448}
				size="paragraph-3"
				weight="regular"
				useTooltip="never"
				text={criteriaData
					.map((data) => `${data.fieldName}: ${formatCriteriaValue(data)}`)
					.join(criteriaItemDelimiter)}
			/>
		</div>
	);
};

/**
 * Determine if criteria is manual or automated and return the appropriate content.
 */
export const getCriteriaCell = (resourceLog: ResourceLogModelFrontend) => {
	const { changeType } = resourceLog;
	switch (changeType) {
		case "add-subject":
		case "remove-subject":
			return getCriteriaManual(resourceLog);
		case "automated-add-subject":
			return getCriteriaAutomated(resourceLog);
		default:
			return null;
	}
};

/**
 * Generate the modal content with automated criteria.
 */
export const getModalContent = (
	{ resourceLog, subjectName }: { resourceLog: ResourceLogModelFrontend; subjectName: string },
	closeFunc: () => void
) => {
	const criteriaData: ReviewCycleAutomatedAssignedSubjectDataFrontend["criteria"] = get(
		resourceLog,
		["data", "criteria"],
		[]
	);

	const subjectId = get(resourceLog, ["data", "subjectId"], null);

	return (
		<Grid className={css.assignedCriteriaModal} cols={1} gap={1}>
			<Text className={css.assignedCriteriaModalTitle} size="heading-5" weight="semi-bold">
				Automation Rules
			</Text>
			<Grid className={css.assignedCriteriaModalItem} cols={2} gap={0}>
				<Text className={css.assignedCriteriaModalField} size="paragraph-3" weight="semi-bold">
					Subject:
				</Text>
				<AvatarProfile
					employeeId={subjectId}
					avatarSize={20}
					employeeName={subjectName || "Unknown"}
					dangerouslySetClassName={css.assignedCriteriaModalSubject}
					forceHardReload
				/>
			</Grid>
			{criteriaData.map((data, index) => (
				<div key={`${data.fieldName}-${index}`} className={css.assignedCriteriaModalItem}>
					<Text className={css.assignedCriteriaModalField} size="paragraph-3" weight="semi-bold">
						{data.fieldName}:
					</Text>
					<Text size="paragraph-3" weight="regular">
						{formatCriteriaValue(data)}
					</Text>
				</div>
			))}
			<Button className={css.assignedCriteriaModalCloseButton} size="medium" onClick={closeFunc}>
				<Text size="paragraph-3" weight="regular">
					Close
				</Text>
			</Button>
		</Grid>
	);
};

export const testables = {
	formatCriteriaValue,
	getSubjectCell,
	getCriteriaManual,
	getCriteriaAutomated,
	getCriteriaCell,
	getModalContent,
};
